import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AddTag, EditTag, Tag, TagParams } from '@app/interfaces/rm/tag';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TagService } from '@services/rm/tag.service';
import { LabelService } from '@services/lm/label.service';

@Component({
    selector: 'app-tag',
    templateUrl: './tag.component.html',
    styleUrls: ['./tag.component.less'],
})
export class TagComponent implements OnChanges {
    @Input() tags: number[] = [];
    @Input() type = '';
    @Input() multiple = true;
    @Input() onlySelect: boolean | undefined;
    @Input() disabled = false;
    @Output() tagsChange = new EventEmitter();

    list: Tag[] = [];
    allList: Tag[] = [];

    loading = false;
    filter = {
        nameLike: '',
    };
    isAdd = false;
    addTagForm: AddTag = {
        name: '',
    };
    editTagForm: Tag = {} as Tag;
    selectTag: Tag = {} as Tag;

    selectOptions: Tag[] = [];
    displayOptions: Tag[] = [];

    inputLoading = false;
    isVisible = false;
    isLmServices = false;
    saveLoading = false;

    constructor(private tagService: TagService,
                private labelService: LabelService,
                private modal: NzModalService) {}

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        try {
            this.isLmServices = ['PROJECT', 'TASK', 'TICKET'].includes(this.type);
            if (this.allList?.length === 0 && this.tags?.length > 0) {
                const params: TagParams = this.isLmServices ? {
                    type: this.type,
                    nameContains: this.filter.nameLike
                } : {
                    resourceType: this.type,
                    name: this.filter.nameLike
                };
                this.inputLoading = true;
                this.allList = this.isLmServices ? await this.labelService.find(params) : await this.tagService.find(params);
            }
            if (changes.tags) {
                this.init();
            }
        } finally {
            this.inputLoading = false;
        }
    }

    init(): void {
        // const ids = this.tags && this.tags.length > 0 ? this.tags.split(',') : [];
        this.selectOptions = this.allList.filter((item) => this.tags?.includes(item.id));
        this.displayOptions = [...this.selectOptions];
        console.log('displayOptions', this.displayOptions, this.allList);
    }

    async fetchData(): Promise<void> {
        try {
            this.loading = true;
            const params: TagParams = this.isLmServices ? {
                type: this.type,
                nameContains: this.filter.nameLike
            } : {
                resourceType: this.type,
                name: this.filter.nameLike
            };
            this.list = this.isLmServices ? await this.labelService.find(params) : await this.tagService.find(params);
            this.allList = [...this.list];
        } finally {
            this.loading = false;
        }
    }

    async submitForm(): Promise<void> {
        try {
            if (this.saveLoading) {
                return;
            }
            this.saveLoading = true;
            const params = this.isLmServices ? {
                name: this.addTagForm.name,
                type: this.type
            } : {
                name: this.addTagForm.name,
                resourceType: this.type
            };
            const response = this.isLmServices ? await this.labelService.add(params) : await this.tagService.add(params);
            console.log('response', response);
            if (response) {
                this.cancelAdd();
                await this.fetchData();
            }
        } finally {
            this.saveLoading = false;
        }
    }

    handleFilter(): void {
        this.openModal();
    }

    openModal(): void {
        this.isVisible = true;
        this.isLmServices = ['PROJECT', 'TASK', 'TICKET'].includes(this.type);
        this.fetchData();
    }

    closeModal(): void {
        this.isVisible = false;
        this.filter.nameLike = '';
        this.resetData();
    }

    resetData(): void {
        this.cancelAdd();
        this.cancelEdit();
        this.init();
    }

    isSelect(tag: Tag): boolean {
        return this.selectOptions.filter((item: Tag) => item.id === tag.id).length > 0;
    }

    onSelect(tag: Tag): void {
        if (!this.multiple) {
            this.selectOptions = [tag];
            return;
        }
        const ids = this.selectOptions.length > 0 ? this.selectOptions.map((item) => item.id) : [];
        const index = ids.indexOf(tag.id);
        if (index === -1) {
            this.selectOptions.push(tag);
        } else {
            this.selectOptions.splice(index, 1);
        }
    }

    removeSelect(index: number): void {
        this.selectOptions.splice(index, 1);
        const tags = this.selectOptions.length > 0 ? this.selectOptions.map((item) => item.id) : [];
        this.tagsChange.emit(tags);
    }

    onSubmit(): void {
        const tags = this.selectOptions.length > 0 ? this.selectOptions.map((item) => item.id) : [];
        this.tagsChange.emit(tags);
        this.closeModal();
    }

    editTag(tag: Tag, event: Event): void {
        event.stopPropagation();
        this.editTagForm = {
            ...tag,
        };
        this.selectTag = {
            ...tag
        };
    }

    cancelEdit(): void {
        this.editTagForm = {
            id: 0,
            name: '',
        };
        this.saveLoading = false;
    }

    cancelAdd(): void {
        this.isAdd = false;
        this.saveLoading = false;
        this.addTagForm = {
            name: '',
        };
    }

    async updateTag(): Promise<void> {
        try {
            if (this.saveLoading) {
                return;
            }
            this.saveLoading = true;
            let params: EditTag = {
                id: this.editTagForm.id,
                name: this.editTagForm.name
            };
            params = this.isLmServices ? {type: this.type, ...params} : {...params};
            const response = this.isLmServices ? await this.labelService.edit(params) : await this.tagService.edit(params);
            if (response) {
                this.cancelEdit();
                await this.fetchData();
            }
        } finally {
            this.saveLoading = false;
        }
    }

    removeTag(tag: Tag, event: Event): void {
        event.stopPropagation();
        this.modal.confirm({
            nzTitle: 'You will delete this label !',
            nzOkText: 'OK',
            nzCancelText: 'Cancel',
            nzOkType: 'primary',
            nzOkDanger: true,
            nzStyle: { top: '40vh' },
            nzOnOk: async () => {
                this.isLmServices ? await this.labelService.remove(tag.id) : await this.tagService.remove(tag.id);
                await this.fetchData();
            },
        });
    }
}
